import React from "react";
import { Box, Breadcrumbs as MuiBreadcrumbs, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

interface BreadcrumbItem {
  label: string;
  path: string;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
}

/**
 * Breadcrumb navigation component
 * Displays navigation path and provides structured data for SEO
 */
export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items }) => {
  if (items.length <= 1) {
    return null; // Don't show breadcrumbs if only one item
  }

  return (
    <Box
      component="nav"
      aria-label="Breadcrumb navigation"
      sx={{
        mb: { xs: 2, md: 3 },
        pt: { xs: 2, md: 3 },
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <MuiBreadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{
          "& .MuiBreadcrumbs-ol": {
            flexWrap: "nowrap",
          },
          "& .MuiBreadcrumbs-li": {
            "&:last-child .MuiTypography-root": {
              fontWeight: 600,
              color: "text.primary",
            },
          },
        }}
      >
        {items.map((item, index) => {
          const isLast = index === items.length - 1;

          if (isLast) {
            return (
              <Typography
                key={item.path}
                color="text.primary"
                sx={{
                  fontFamily: '"Playfair Display", serif',
                  fontSize: { xs: "0.875rem", md: "0.9375rem" },
                }}
              >
                {item.label}
              </Typography>
            );
          }

          return (
            <Link
              key={item.path}
              component={RouterLink}
              to={item.path}
              underline="hover"
              color="text.secondary"
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontSize: { xs: "0.875rem", md: "0.9375rem" },
                "&:hover": {
                  color: "text.primary",
                },
              }}
            >
              {item.label}
            </Link>
          );
        })}
      </MuiBreadcrumbs>
    </Box>
  );
};

/**
 * Hook to generate breadcrumb items based on current route
 */
export function useBreadcrumbs(): BreadcrumbItem[] {
  const pathnames = window.location.pathname.split("/").filter((x) => x);

  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Home", path: "/" },
  ];

  let currentPath = "";
  pathnames.forEach((pathname) => {
    currentPath += `/${pathname}`;
    
    // Format label (capitalize, replace hyphens with spaces)
    const label = pathname
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    breadcrumbs.push({
      label,
      path: currentPath,
    });
  });

  return breadcrumbs;
}
